import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Box,
  Divider,
  Grid,
  ListItem,
  ListItemIcon,
  ListItemText,
  Radio,
  Typography,
  Skeleton,
} from "@mui/material";
import {
  PrimaryButton,
  OutlinedPrimaryButton,
} from "../../../../components/ui/Button";
import { useDispatch } from "react-redux";
import { PaymentTermLoanContext } from "../../../../hooks/context";
import { InputField } from "../../../../components/ui/Inputs";
import WestIcon from "@mui/icons-material/West";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { RadioInputField } from "../../../../components/ui/Inputs";
import postsServices from "../../../../services/api/service/postsService";
import { radioLabel } from "../../../../components/ui/commonCss";

export interface RowData {
  [key: string]: string | number | null;
}

const label = { inputProps: { "aria-label": "Checkbox demo" } };

const AdditionalDetailsLoan = () => {
  const { setFormStep, formValue, handleFormValue, isXsOrSm }: any = useContext(
    PaymentTermLoanContext
  );

  useEffect(() => {
      // Scroll to the top of the page when the component mounts
      window.scrollTo({
        top: 0,
        behavior: "smooth", // Adds smooth scrolling
      });
    }, []);

  const [fundsData, setFundsData] = useState<any>([]);
  const [desiredData, setDesiredData] = useState<any>([]);
  const [isLoading, setIsLoading] = useState(true);

  /**
   * Fetches finance-related data from the server.
   *
   * This function makes an asynchronous call to the endpoint `/api/v1/payment/get-source-funds`
   * to retrieve the source of funds data. The data is then stored in the state using `setFundsData`.
   *
   * @async
   * @function fetchGetFinanceRestData
   * @returns {Promise<void>} A promise that resolves when the data fetching is complete.
   * @throws Will log an error to the console if the request fails.
   */
  const fetchFundDataList = async (): Promise<void> => {
    try {
      await postsServices
        .getPosts("/api/v1/payment/get-source-funds")
        .then((response: any) => {
          setFundsData(response?.data);
        })
        .catch((error: any) => {
          console.log(error);
        });
    } catch (error: any) {
      console.log(error);
    }
  };

  /**
   * Fetches the desired list data from the server.
   *
   * This function makes an asynchronous call to the endpoint `/api/v1/payment/get-desired-impacts`
   * to retrieve the desired impacts data. The response data is then set to the state using `setFundsData`.
   *
   * @async
   * @function fetchDesiredListData
   * @returns {Promise<void>} A promise that resolves when the data fetching is complete.
   * @throws Will log an error to the console if the request fails.
   */
  const fetchDesiredListData = async (): Promise<void> => {
    try {
      await postsServices
        .getPosts("/api/v1/payment/get-desired-impacts")
        .then((response: any) => {
          setDesiredData(response?.data);
          setIsLoading(false);
        })
        .catch((error: any) => {
          setIsLoading(false);
        });
    } catch (error: any) {
      setIsLoading(false);
      console.log(error);
    }
  };

  useEffect(() => {
    fetchFundDataList();
    fetchDesiredListData();
  }, []);

  const isCheckValue = useMemo(() => {
    return (
      !!formValue?.sourceFundsUuid &&
      !!formValue?.reasonForPartPayment &&
      !!formValue?.desiredImpactUuid
    );
  }, [formValue]);

  return (
    <Grid item xs={12} sm={12} md={12} lg={12} className="form-container">
      <Box>
        <Typography className="section-sub-heading mb-10 mt-20">
          ADDITIONAL DETAILS
        </Typography>
      </Box>

      <Grid container item mb={2} sx={{ paddingTop: "16px" }}>
        <Typography className="disbursement-inside-page-lable-condtion-dark">
          Please select the source of funds for the Part Pre-Payment *
        </Typography>
      </Grid>

      <Grid container>
        {isLoading
          ? // Render skeletons while loading
          Array.from(new Array(6)).map((_, index) => (
            <Grid item xs={12} sm={4} md={4} lg={4} key={index}>
              <ListItem>
                <ListItemIcon>
                  <Skeleton
                    variant="circular"
                    width={24}
                    height={24}
                    animation="wave"
                  />
                </ListItemIcon>
                <ListItemText
                  primary={
                    <Skeleton
                      variant="text"
                      width="80%"
                      animation="wave"
                      sx={{
                        fontSize: "11px",
                      }}
                    />
                  }
                />
              </ListItem>
            </Grid>
          ))
          : // Render actual data when available
          fundsData?.map((elem: any, index: number) => (
            <Grid item xs={12} sm={4} md={4} lg={4} key={index}>
              <Box display="flex" alignItems="center" sx={{ height: "36px" }}>
                <RadioInputField
                  checked={formValue?.sourceFundsUuid === elem?.uuid}
                  name={`name_${formValue?.sourceFundsUuid}`}
                  onClick={() =>
                    handleFormValue("sourceFundsUuid", elem?.uuid)
                  }
                  value={formValue?.sourceFundsUuid}
                />
                <Typography sx={radioLabel?.additionLabel}>{elem?.name}</Typography>
              </Box>
            </Grid>
          ))}
      </Grid>

      <div>
        <Grid container item mb={2} sx={{ paddingTop: "25px" }}>
          <Typography className="disbursement-inside-page-lable-condtion-dark">
            Please specify the reason for the Part Pre-Payment *
          </Typography>
        </Grid>
        <Grid container item spacing={2} mb={2}>
          <Grid item xs={12} sm={12} md={12} lg={12}>
            {isLoading ? (
              <Skeleton
                variant="rectangular"
                width="100%"
                height={40}
                animation="wave"
                sx={{ borderRadius: "6px" }}
              />
            ) : (
              <InputField
                fullWidth
                type="text"
                autoComplete="off"
                placeholder="Enter here"
                onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                  handleFormValue("reasonForPartPayment", event.target.value)
                }
                value={formValue?.reasonForPartPayment || ""}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "6px",
                  },
                }}
              />
            )}
          </Grid>
        </Grid>
      </div>

      <div>
        <Grid container>
          <Grid item xs={12} sm={3} md={4} lg={3} mt={2}>
            <Typography className="disbursement-inside-page-lable-condtion-dark">
              Please select the desired impact *
            </Typography>
          </Grid>
          {isLoading
            ? // Render skeleton loader
            Array.from(new Array(6)).map((_, index) => (
              <Grid item xs={3} sm={3} md={3} lg={3} key={index}>
                <ListItem>
                  <ListItemIcon>
                    <Skeleton
                      variant="circular"
                      width={24}
                      height={24}
                      animation="wave"
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Skeleton
                        variant="text"
                        width="80%"
                        animation="wave"
                        sx={{
                          fontSize: "11px",
                        }}
                      />
                    }
                  />
                </ListItem>
              </Grid>
            ))
            : // Render actual data
            desiredData?.map((elem: any, index: number) => (
              <Grid item xs={5} sm={3} md={3} lg={3} key={index} sx={{ paddingTop: "7px" }}>
                <Box display="flex" alignItems="center">
                  <RadioInputField
                    checked={formValue?.desiredImpactUuid === elem?.uuid}
                    name={`name_${formValue?.desiredImpactUuid}`}
                    onClick={() =>
                      handleFormValue("desiredImpactUuid", elem?.uuid)
                    }
                    value={formValue?.desiredImpactUuid}
                  />
                  <Typography
                    sx={isXsOrSm ? radioLabel?.labelDarkMobile : radioLabel?.labelDark}
                  >
                    {elem?.name}
                  </Typography>
                </Box>
              </Grid>
            ))}
        </Grid>
      </div>
      <Grid
        container
        justifyContent="center" // Center the buttons horizontally
        alignItems="center"
        spacing={2} // Maintain spacing between items
        mt={1}
        mb={isXsOrSm ? 2 : 0}
      >
        <Grid item xs={6} sm={6} md={6} lg={6} container justifyContent="left">
          <OutlinedPrimaryButton
            width={isXsOrSm ? "112px" : "129px"}
            height={isXsOrSm ? "30px" : "42px"}
            fullWidth={true}
            onClick={() => setFormStep(0)}
          >
            <WestIcon sx={{ fontSize: "17px" }} /> Previous
          </OutlinedPrimaryButton>
        </Grid>
        <Grid
          item
          xs={6}
          sm={6}
          md={6}
          lg={6}
          container
          justifyContent="right"
        >
          <PrimaryButton
            fullWidth={true}
            width={isXsOrSm ? "111px" : "127px"}
            height={isXsOrSm ? "30px" : "42px"}
            onClick={() => setFormStep(2)}
            disabled={isLoading || !isCheckValue}
          >
            Proceed &nbsp; <ArrowForwardIcon sx={{ fontSize: "17px" }} />
          </PrimaryButton>
        </Grid>
      </Grid>
    </Grid>
  );
};

export default AdditionalDetailsLoan;
